import * as React from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";

import { HStack, Link, Typography, useTheme } from "@smartrent/ui";
import { Pencil } from "@smartrent/icons";

import { useDrawerNav } from "@/common/AppDrawer";
import { useTppViewQuery, useAlloyAccessSiteQuery } from "@/api";
import { StatusBadgeDetailView } from "@/common/StatusBadgeDetailView";
import { TppViewProps } from "@/types/ThirdPartyProvidersViewProps";

import { useGroup } from "../../GroupContext";

export interface GroupAlloyAccessModuleProps {
  containerStyle: any;
}

type AlloyAccessSystemType = "alloy_access" | "salto";

const ALLOY_ACCESS_SYSTEM_TYPES: Array<{
  systemType: AlloyAccessSystemType;
  label: string;
  providerName: string;
}> = [
  {
    systemType: "alloy_access",
    label: "Alloy Access",
    providerName: "Alloy Access",
  },
  {
    systemType: "salto",
    label: "Alloy Access Salto",
    providerName: "Alloy Access - Salto",
  },
];

interface AlloyAccessProviderSectionProps {
  systemType: AlloyAccessSystemType;
  label: string;
  record?: TppViewProps;
  groupId: number;
  isFirst: boolean;
}

const AlloyAccessProviderSection: React.FC<AlloyAccessProviderSectionProps> = ({
  systemType,
  label,
  record,
  groupId,
  isFirst,
}) => {
  const drawer = useDrawerNav();
  const { colors } = useTheme();

  const existingSiteId: string | null =
    record?.custom_provider_configuration?.site_id ?? null;
  const existingEnabled: boolean = record?.enabled ?? false;

  const { data: existingSiteData, isLoading: isLoadingExistingSite } =
    useAlloyAccessSiteQuery(
      { groupId, siteId: existingSiteId },
      { enabled: !!existingSiteId }
    );

  const existingSiteRecord = existingSiteData?.records?.[0] ?? null;

  const goToDrawer = () =>
    drawer.push("editAlloyAccessIntegration", { systemType });

  return (
    <View style={!isFirst ? styles.sectionSpacing : undefined}>
      <View style={[styles.header, { borderBottomColor: colors.gray200 }]}>
        <HStack spacing={8} style={styles.titleGroup}>
          <Typography type="title5">{label}</Typography>
          {record ? (
            <TouchableOpacity onPress={goToDrawer}>
              <Pencil size={16} color={colors.primary} />
            </TouchableOpacity>
          ) : null}
        </HStack>
        <StatusBadgeDetailView condition={existingEnabled} />
      </View>

      {record ? (
        existingSiteId ? (
          <View style={styles.content}>
            <Typography>
              {isLoadingExistingSite ? (
                `site id: ${existingSiteId} (loading...)`
              ) : existingSiteRecord ? (
                <Link
                  href={existingSiteRecord.site_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.link}
                >
                  {existingSiteRecord.name}
                </Link>
              ) : (
                `site id: ${existingSiteId}`
              )}
            </Typography>
            {!isLoadingExistingSite && existingSiteRecord ? (
              <Typography>
                (Org ID: {existingSiteRecord.organization_id}, Site ID:{" "}
                {existingSiteId})
              </Typography>
            ) : null}
          </View>
        ) : null
      ) : (
        <View style={styles.content}>
          <TouchableOpacity onPress={goToDrawer}>
            <Typography style={styles.link} color="link">
              Link
            </Typography>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

export const GroupAlloyAccessModule: React.FC<
  React.PropsWithChildren<GroupAlloyAccessModuleProps>
> = ({ containerStyle }) => {
  const { group } = useGroup();

  const { data: tppViewData } = useTppViewQuery({
    filters: { groupId: group.id, tppc_scope: "group" },
  });

  return (
    <View style={containerStyle}>
      {ALLOY_ACCESS_SYSTEM_TYPES.map(
        ({ systemType, label, providerName }, index) => {
          const record = tppViewData?.records?.find(
            (r) => r.provider === providerName
          );

          return (
            <AlloyAccessProviderSection
              key={systemType}
              systemType={systemType}
              label={label}
              record={record}
              groupId={group.id}
              isFirst={index === 0}
            />
          );
        }
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  sectionSpacing: {
    marginTop: 32,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  titleGroup: {
    alignItems: "center",
  },
  content: {
    paddingTop: 8,
    paddingBottom: 8,
  },
  link: { textDecorationLine: "underline" },
});
